import { Component, OnInit, inject, signal, computed, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { filter, skip } from 'rxjs/operators';

import { Formation } from '../../../../../../shared/src/lib/model/formation.model';
import { AppStateStudent } from '../../../student.states';
import { selectAllFormations, selectSelectedFormation } from '../../../../../../shared/src/lib/store/selectors/formation.selectors';
import * as FormationActions from '../../../../../../shared/src/lib/store/actions/formation.actions';
import { FeatureModule } from '../../../../../../shared/src/lib/feature/feature.module';

/**
 * Formation Switcher Component
 * Menu déroulant pour sélectionner une formation dans le header
 * Utilise le store NgRx pour la liste et la sélection
 */
@Component({
  selector: 'app-formation-switcher',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FeatureModule
  ],
  templateUrl: './formation-switcher.component.html',
  styleUrls: ['./formation-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormationSwitcherComponent implements OnInit {
  private store = inject(Store<AppStateStudent>);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Signals
  formations = signal<Formation[]>([]);
  selectedFormation = signal<Formation | null>(null);
  loading = signal(false);
  isInitialized = signal(false); // Pour savoir si c'est la première charge

  // FormControl pour mat-select
  formationControl = new FormControl<number | null>(null);

  // Computed
  hasFormations = computed(() => this.formations().length > 0);
  hasNonActiveFormations = computed(() =>
    this.formations().some(f => f.status !== 'active')
  );
  hasActiveFormations = computed(() =>
    this.formations().some(f => f.status === 'active')
  );
  hasPendingFormations = computed(() =>
    this.formations().some(f => f.status === 'pending')
  );
  hasDoneFormations = computed(() =>
    this.formations().some(f => f.status === 'done')
  );
  hasCancelledFormations = computed(() =>
    this.formations().some(f => f.status === 'cancelled')
  );
  hasSuspendedFormations = computed(() =>
    this.formations().some(f => f.status === 'suspended')
  );

  displayText = computed(() => {
    const formation = this.selectedFormation();
    if (!formation) return 'Aucune formation';
    return formation.name || 'Formation sans nom';
  });

  ngOnInit(): void {
    // Charger les formations depuis le store
    this.store.select(selectAllFormations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(formations => {
        this.formations.set(formations);
      });

    // Écouter la formation sélectionnée
    this.store.select(selectSelectedFormation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(formation => {
        if (formation) {
          console.log('Formation sélectionnée:', formation);
          this.selectedFormation.set(formation);
          this.formationControl.setValue(formation.id, { emitEvent: false });
          
          // Rediriger seulement si ce n'est pas la première initialisation
          if (!this.isInitialized()) {
            console.log('Formation changée, redirection...');
            this.redirectToFormation(formation);
          } else {
            this.isInitialized.set(true);
          }
        }
      });

    // Écouter les changements du select
    this.formationControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(formationId => {
        if (formationId !== null) {
          this.onFormationChange(formationId);
        }
      });
  }

  /**
   * Gère le changement de formation
   */
  private onFormationChange(formationId: number): void {
    const formation = this.formations().find(f => f.id === formationId);
    if (formation) {
      // Sauvegarder l'ID dans localStorage pour persistance
      localStorage.setItem('last_selected_formation_id', formation.id.toString());
      
      // Dispatch action pour sélectionner la formation
      this.store.dispatch(FormationActions.SelectFormation({ formation }));
    }
  }

  /**
   * Redirige vers la bonne route selon le statut de la formation
   * Réplique la logique du header legacy (goToFormation)
   */
  private redirectToFormation(formation: Formation): void {
    if (!formation) return;

    const currentUrl = this.router.url;
    let targetUrl = '';

    switch (formation.status) {
      case 'pending':
        targetUrl = `/plan-formation/introduction`;
        break;
      case 'suspended':
        targetUrl = `/dashboard`;
        break;
      case 'active':
      default:
        targetUrl = `/dashboard`;
        break;
    }

    // Ne naviguer que si on n'est pas déjà sur la bonne route
    if (!currentUrl.includes(targetUrl)) {
      console.log('Navigation vers:', targetUrl);
      this.router.navigate([targetUrl]);
    } else {
      console.log('Déjà sur la bonne route:', currentUrl);
    }
  }

  /**
   * Retourne les classes CSS pour le badge de statut
   */
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
      case 'done':
        return 'bg-slate-50 text-slate-700 ring-1 ring-slate-200';
      case 'suspended':
        return 'bg-red-50 text-red-700 ring-1 ring-red-200';
      case 'cancelled':
        return 'bg-gray-50 text-gray-700 ring-1 ring-gray-200';
      default:
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';
    }
  }

  /**
   * Retourne le label du statut
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      active: 'En cours',
      pending: 'En attente',
      done: 'Terminée',
      suspended: 'Suspendue',
      cancelled: 'Annulée',
      missing_information: 'Informations manquantes'
    };
    return labels[status] || status;
  }
}
